import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Switch,
  IconButton,
  Alert,
  Typography,
  Tabs,
  Tab,
  Paper,
  Tooltip,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { createFeatureFlag, deleteFeatureFlag, getFeatureFlag } from './services';
import { AllowByUserRole } from '../../../components/permission';
import { RiAlertLine } from 'react-icons/ri';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from '../../../components/pageHeader';
import SettingsNav from '../settingsNav';
import PropTypes from 'prop-types';
import obelisk from '../../../clients/obelisk';
import useFlagsHook from '../../../hooks/useFlags.hook';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import CustomToolbar from './CustomToolBar';
import FeatureFlagEditModal from './FeatureFlagEditModal';
import { ModalView } from '../../../providers/modal.provider';
import FeatureFlagDeleteModal from './FeatureFlagDeleteConfirmation';
import { getDataGridLocaleText } from '../../../hooks/Helper';

const featureList = {
  deprecate: [
    {
      name: 'New Maitryx Widget',
      key: 'MAITRYX_WIDGET_V2',
      description: 'Replaces old version.',
    },
    {
      name: 'Report Center',
      key: 'REPORT_CENTER',
      description: 'Replaces old version.',
    },
    {
      name: 'Add Card Payment',
      key: 'NEW_PAYMENT_METHOD',
      description: 'Toggles payment button',
    },
    {
      name: 'Left Nav Academy Link',
      key: 'NEW_ACADEMY_REDIRECT',
      description: 'Redirects Academy link in left nav to new Academy site',
    },
    {
      name: 'Login',
      key: 'LOGIN_PAGE',
      description: 'Activates new login modal',
    },
    {
      name: 'Search Order Page',
      key: 'SEARCH_ORDER_PAGE',
      description: 'Activates new search order page',
    },
    {
      name: 'Search Order Dropdown',
      key: 'SEARCH_ORDER_DROPDOWN',
      description: 'Toggles new search order dropdown',
    },
    {
      name: 'Commission Report Date',
      key: 'COMMISSION_REPORT_DATE',
      description: 'Not sure but is accessible in the periodPicker.js file',
    },
    {
      name: 'Enhanced E-Wallet',
      key: 'ENHANCE_EWALLET',
      description:
        'Toggle between new layout of e-wallet and old layout when we have tygaUserId available. Allows "1" or "0"',
    },
    {
      name: 'Referral Widget',
      key: 'FEATURE_REFERRAL',
      description: 'Toggles referral view in widget',
    },
    {
      name: 'Strm Widget',
      key: 'STRM_WIDGET',
      description: 'Toggles STRM old vs new widget',
    },
    {
      name: 'Services Nav',
      key: 'SERVICES_PAGE',
      description: 'Toggles services navigation and other related pages',
    },
    {
      name: 'Admin Alerts',
      key: 'ADMIN_ALERTS',
      description: 'Allows access to the admin alerts feature. Allows "1" or "0"',
    },
    {
      name: 'Skip Alerts',
      key: 'SKIP_ALERT_CHECK',
      description: 'Allows access to the skip alerts feature. Allows "1" or "0"',
    },
    {
      name: 'Skip Button',
      key: 'SKIP_BUTTON',
      description:
        'Allows access to the skip button feature. Will allow login skipping for events. Allows "1" or "0"',
    },
    {
      name: 'Fix Country Phonecode',
      key: 'FIX_COUNTRY_PHONECODE',
      description:
        'Allows access to the fix country phonecode feature. Allows "1" or "0"',
    },
    {
      name: 'Checkout Enhance Coupon',
      key: 'CHECKOUT_ENHANCE_COUPON',
      description:
        'Allows access to the checkout enhance coupon feature. Allows "1" or "0"',
    },
    {
      name: 'Toggle Autoship Design',
      key: 'REDESIGN_AUTOSHIP',
      description:
        'Allows access to the toggle autoship redesign feature. Allows "1" or "0"',
    },
    {
      name: 'Enhance Order Data',
      key: 'ENHANCE_ORDER_DETAIL',
      description: 'Not sure. Found in customerOrder.js file. Allows "1" or "0"',
    },
    {
      name: 'Order Data',
      key: 'ORDER_DETAIL',
      description: 'Not sure. Found in orderDetail.js file. Allows "1" or "0"',
    },
    {
      name: 'Enhance Subscription',
      key: 'ENHANCE_SUBSCRIPTION',
      description:
        'Not sure. Found in pages/customers/Services/services.js file. Allows "1" or "0"',
    },
    {
      name: 'Toggle User Summary View',
      key: 'TREE_REDIRECT',
      description:
        'Allows the redirect to user-summary page or just summary page. Allows "1" or "0"',
    },
    {
      name: 'Coupon Percent',
      key: 'COUPON_PERCENT',
      description: 'Not sure. Found on coupon.js file. Allows "1" or "0"',
    },
    {
      name: 'Coupon Card UI',
      key: 'COUPON_CARDUI',
      description: 'Not sure. Found on coupon.js file. Allows "1" or "0"',
    },
    {
      name: 'Redesign Teams Page',
      key: 'REDESIGN_TEAMS',
      description: 'Toggles new Team page design. Allows "1" or "0"',
    },
    {
      name: 'Payment Method',
      key: 'PAYMENTS_METHOD',
      description:
        'Not sure. Found on settings.protected.route.js file. Allows "1" or "0"',
    },
    {
      name: 'Commission Projection Widget',
      key: 'COMMISSION_PROJECTIONS',
      description: 'Allows widget to be available. Allows "1" or "0"',
    },
    {
      name: 'Use New Tree',
      key: 'PILLARS_TREE',
      description: 'Allows tree to be available. Allows "1" or "0"',
    },
    {
      name: 'Site Builder',
      key: 'SITE_BUILDER',
      description: 'Allows site builder to be available. Allows "1" or "0"',
    },
    {
      name: 'Dark Mode',
      key: 'DARKMODE',
      description: 'Allows dark mode to be available. Allows "1" or "0"',
    },
    {
      name: 'Direct Edit Products',
      key: 'PROD_DIRECT_EDIT',
      description: 'Allows link activated on product card. Allows "1" or "0"',
    },
    {
      name: 'Enhance Coupon Code in Checkout',
      key: 'CHECKOUT_ENHANCE_COUPON',
      description:
        'Allows access to the enhanced coupon code feature and design in all checkout. Allows "1" or "0"',
    },
    {
      name: 'New Order Detail Page',
      key: 'ORDER_DETAIL',
      description: 'Allows access to the new order detail page layout. Allows "1" or "0"',
    },
    {
      name: 'New Edit Subscription Form and create Subscription visiblility',
      key: 'ENHANCE_SUBSCRIPTION',
      description:
        'Allows access to the new edit subscription form layout and create subscription visibility feature. Allows "1" or "0"',
    },
    {
      name: 'Eacho Access',
      key: 'EACHO_WIDGET',
      description: 'Allows access to the new Eacho widget . Allows [string]',
    },
    {
      name: 'Marketing Site Tab Bar and Sku links Access',
      key: 'MARKETING_SITE_SKU_LINKS',
      description:
        'Allows access to the Marketing Site Tab Bar and Sku links in the marketing site widget. Allows "1" or "0"',
    },
    {
      name: 'Grouping layout of the Services inside Subscription',
      key: 'ORDER_SUBSCRIPTION_SERVICE_ACCORDION',
      description:
        'Displays services under their parent subscription using an accordion layout on the Order & Subscription page. Allows "1" or "0".',
    },
    {
      name: 'Left Nav Toggles Icon',
      key: 'SIDEBAR_TOGGLE',
      description: 'Toggles the left navigation arrow icon. Allows "1" or "0"',
    },
    {
      name: 'Toggle Raw UTC Datetime Without Timezone',
      key: 'EARNINGS_RANK_SYNC_FIX',
      description:
        'Toggles the use of UTC datetime without timezone on widgets and other features. Allows "1" or "0"',
    },
    {
      name: 'Report Center Active Reports Restriction',
      key: 'REPORT_CENTER_RESTRICT_ACTIVE_REPORTS',
      description:
        'Restricts Report Center access so only Associate Report is active while Order and Autoship reports are shown as Coming Soon. Allows "1" or "0"',
    },
    {
      name: 'Translation Tool Feature',
      key: 'TRANSLATION_TOOL',
      description: 'Allows access to the translation tool feature. Allows "1" or "0"',
    },
    {
      name: 'Date Picker Monthly Mode',
      key: 'DATE_PICKER_MONTHLY',
      description:
        'Enables monthly date picker options instead of weekly options. Shows Today, 1-Month Ago, 2-Month Ago, and 3-Month Ago when enabled. Shows weekly ranges when disabled. Allows "1" or "0".',
    },
    {
      name: 'Feature Flag Definitions Management',
      key: 'FEATURE_FLAG_DEFINITIONS_MANAGEMENT',
      description:
        'Enables search, sorting, pagination, and editing of feature flag definitions in the admin portal. Allows "1" or "0".',
    },
    {
      name: 'Deprecated Flags Management',
      key: 'DEPRECATED_FLAGS_MANAGEMENT',
      description:
        'Enables dedicated deprecated flags page with status tracking, assignee ownership, search, filtering, and pagination. Allows "1" or "0".',
    },
    {
      name: 'Feature Flag Definition Popup',
      key: 'FEATURE_FLAG_DEFINITION_POPUP',
      description:
        'Adds a definition icon to the Feature Flags table that opens a popup displaying the selected feature flag definition. Allows "1" or "0".',
    },
  ],
  features: [
    {
      name: 'Marketing Site Widget SKU Permission',
      key: 'MARKETING_SITE_SUBSCRIPTION_IDS',
      description:
        'Activates the marketing site widget on the dashboard for associates with specific subscription IDs. Allows a comma-separated list of subscription IDs. Example: "123,456" would only allow associates with subscription IDs 123 and 456 to see the marketing site widget.',
    },
    {
      name: 'Main Dashboard Banner',
      key: 'MAIN_DASHBOARD_BANNER{_EN}',
      description:
        'Activates the main dashboard banner. Allows [string] Url to the banner image. You can also add a language code to the end of the key to have different banners for different languages, example: MAIN_DASHBOARD_BANNER_EN for English and MAIN_DASHBOARD_BANNER_ES for Spanish.',
    },
    {
      name: 'Main Dashboard Banner for Mobile',
      key: 'MAIN_DASHBOARD_BANNER_MOBILE{_EN}',
      description:
        'Activates the main dashboard banner for mobile. Allows [string] Url to the banner image. You can also add a language code to the end of the key to have different banners for different languages, example: MAIN_DASHBOARD_BANNER_MOBILE_EN for English and MAIN_DASHBOARD_BANNER_MOBILE_ES for Spanish.',
    },
    {
      name: 'Main Dashboard Banner URL',
      key: 'MAIN_DASHBOARD_BANNER_URL{_EN}',
      description:
        'The url that the banner button will link to when clicked. Allows [string] Url. You can also add a language code to the end of the key to have different links for different languages, example: MAIN_DASHBOARD_BANNER_URL_EN for English and MAIN_DASHBOARD_BANNER_URL_ES for Spanish.',
    },
    {
      name: 'Main Dashboard Banner Button Text',
      key: 'MAIN_DASHBOARD_BANNER_BTN_TEXT',
      description:
        'This is the button text for the main dashboard banner. You must use an existing translation key for the value. Allows [string] Example values could be "joinNow", "registerNow", or "connect".',
    },
    {
      name: 'Main Dashboard Banner Button Strip Color',
      key: 'MAIN_DASHBOARD_BANNER_BTN_STRIP_COLOR',
      description:
        'This is the button strip color for the main dashboard banner. You must use an existing color key for the value. Allows [string] Example values should be compatible with the existing color palette (hex, rgb, rgba).',
    },
    {
      name: 'Testing Environment',
      key: 'TESTING_ENV',
      description:
        'This will allow a flag to only be accessible to ClientAdmin and Admin user roles so toggling on a feature will only display to those roles. These should be used in any environment where data is affecting production. Must be "' +
        window.location.hostname +
        '"',
    },
    {
      name: 'Report Center Filter Menu',
      key: 'REPORT_MENU_MAX_HEIGHT',
      description:
        'Allows the height of the report center filter menu to be configured. Allows a numeric value representing the maximum height in pixels.',
    },
    {
      name: 'Business Hub Associate ID Filter',
      key: 'BUSINESS_HUB_FILTER',
      description:
        'Denies access to the business hub. Format of the flag is "[number],[number]". Example: "123,456" would not allow associates with IDs 123 and 456 to access the business hub.',
    },
    {
      name: 'KYC Features',
      key: 'KYC_ADMIN',
      description: 'Allows access to Admin and Associate KYC features. Allows "1" or "0"',
    },
    {
      name: 'Pay Week',
      key: 'PAYWEEK_MOD',
      description: 'Allows access to the payweek features. Allows "1" or "0"',
    },
    {
      name: 'Associate Logs Viewer',
      key: 'LOG_VIEWER',
      description:
        'Allows access to the associate logs viewer features. Allows "1" or "0"',
    },
    {
      name: 'Tree Logs Viewer',
      key: 'TREE_VIEW_LOG',
      description: 'Allows access to the tree logs viewer features. Allows "1" or "0"',
    },
    {
      name: 'Shared Reports',
      key: 'REPORT_CENTER_TEAM_REPORTS',
      description: 'Allows access to the shared reports features. Allows "1" or "0"',
    },
    {
      name: 'Telegram Deauth Service',
      key: 'TELEGRAM_DEAUTH',
      description: 'Allows access to the Telegram deauth service. Allows "1" or "0"',
    },
    {
      name: 'Troubleshoot Token Expire Feature',
      key: 'DEBUGGING',
      description:
        'Allows access to the troubleshoot token expire feature. Allows "1" or "0"',
    },
    {
      name: 'Toggle Language Icons',
      key: 'ENHANCE_LANGUAGE_FLAG',
      description:
        'Allows access to the toggle language icons feature. Allows "1" or "0"',
    },
    {
      name: 'Forex Banner Activation',
      key: 'BANNERS_ENABLED',
      description: 'Allows access to the forex banner feature. Allows [string]',
    },
    {
      name: 'Forex Banner URL Link',
      key: 'FOREX_BANNER_URL',
      description: 'Makes the FOREX banner have an external URL link. Allows [string]',
    },
    {
      name: 'Forex Banner Button Text',
      key: 'FOREX_BANNER_BUTTON_TEXT',
      description:
        'Sets the text for the FOREX banner button. **NOTE** You must use an existing translation key. Allows [string | connect | registerNow | joinNow ]',
    },
    {
      name: 'Forex Banner Button Activation',
      key: 'FOREX_BANNER_BUTTON',
      description:
        'Activates the FOREX banner button and bar along the bottom. Allows "1" or "0"',
    },
    {
      name: 'Business Hub Banner',
      key: 'BH_BANNERS_ENABLED',
      description: 'Allows access to the business hub banner feature. Allows [string]',
    },
    {
      name: 'Business Hub Banner Image Path',
      key: 'BH_BANNER_IMG_PATH_{LANGUAGE} || BH_BANNER_IMG_PATH',
      description:
        'Allows for changing the business hub banner image path. Allows [string]',
    },
    {
      name: 'Business Hub Banner Image Path (Mobile)',
      key: 'BH_BANNER_IMG_PATH_MOB_{LANGUAGE} || BH_BANNER_IMG_PATH_MOB',
      description:
        'Allows for changing the business hub banner image path for mobile view. Allows [string]',
    },
    {
      name: 'Business Hub Banner Link',
      key: 'BH_BANNER_LINK_{LANGUAGE} || BH_BANNER_LINK',
      description: 'Allows for changing the business hub banner link. Allows [string]',
    },
    {
      name: 'Business Hub Banner Description',
      key: 'BH_BANNER_DESC_{LANGUAGE} || BH_BANNER_DESC',
      description:
        'Allows for changing the business hub banner description. Allows [string]',
    },

    {
      name: 'Telegram Deauth Service',
      key: 'TELEGRAM_DEAUTH',
      description:
        'Allows access to the Telegram deauth service button for Trade Ideas. Allows "1" or "0"',
    },
    {
      name: 'Language Flag Shown',
      key: 'ENHANCE_LANGUAGE_FLAG',
      description:
        'Allows access to show the flags in the Language dropdown. Allows "1" or "0"',
    },
    {
      name: 'Search Order Page Access',
      key: 'SEARCH_ORDER_PAGE',
      description: 'Allows access to the search order page. Allows "1" or "0"',
    },
    {
      name: 'Admin Alerts Access',
      key: 'ADMIN_ALERTS',
      description:
        'Allows access to the admin alerts page and GetNeededAlert api during login. Allows "1" or "0"',
    },
    {
      name: 'Phone Country Code Fix',
      key: 'FIX_COUNTRY_PHONECODE',
      description:
        'Fixes the country code and include phone code in Add Card Form so user can not change it and have phoneCode. Allows "1" or "0"',
    },
    {
      name: 'Commission Projections Access',
      key: 'COMMISSION_PROJECTIONS',
      description:
        'Allows access to the commission projections widget on Business Hub. Allows "1" or "0"',
    },
    {
      name: 'Leaderboard Access',
      key: 'LEADERBOARD',
      description:
        'Allows access to the leaderboard widget on Business Hub. Allows "1" or "0"',
    },
    {
      name: 'Skip Alert Access',
      key: 'SKIP_ALERT_CHECK',
      description: 'Allows users to skip all alerts during login . Allows "1" or "0"',
    },
    {
      name: 'Skip Button Access',
      key: 'SKIP_BUTTON',
      description:
        'Allows users to see the skip button on alerts during login so they can skip them. Allows "1" or "0"',
    },
    {
      name: 'Access to Edit Subscription and Services in product',
      key: 'EDIT_SUBSCRIPTION_SERVICES',
      description:
        'Allows access to edit subscription and services in product . Allows "1" or "0"',
    },
    {
      name: 'Event Management Feature',
      key: 'ENABLE_EVENT_MANAGEMENT',
      description:
        'Allow access to create a status event in settings. Found on statusByEvent.js file. Allows "1" or "0"',
    },
    {
      name: 'Autoship Frequency and Periods (Admin)',
      key: 'AUTOSHIP_FREQUENCY_PERIODS_ADMIN',
      description:
        'Allows Admin users to optionally set frequency and periods on the Autoship Checkout page when creating an autoship.',
    },
    {
      name: 'Coupon UI Label Update',
      key: 'COUPON_UI_LABEL_UPDATE',
      description:
        'Updates coupon UI labels by removing the Unknown tag, displaying Affiliate instead of Associate, and changing Items to Product with product name and item ID display.',
    },
    {
      name: 'Enable Access to Update Services with Update Subscription',
      key: 'ENABLE_UPDATE_SERVICES_WITH_SUBSCRIPTION',
      description:
        'Controls whether admins can update associated services when modifying a subscription expiration date. Allows "1" or "0"',
    },
    {
      name: 'Autoship Monthly Date Display (Client 3)',
      key: 'AUTOSHIP_MONTHLY_DISPLAY_CLIENT3',
      description:
        'Controls how the next process date is displayed on the Autoship Checkout page for Client 3 (Italy), allowing it to reflect a monthly interval instead of a 28-day cycle.',
    },
    {
      name: 'Subscription and Service Tabs',
      key: 'SUBSCRIPTION_SERVICE_TABS',
      description:
        'Separates the Subscriptions page into dedicated Subscriptions and Services tabs using parent subscription and service structure.',
    },
    {
      name: 'Organization Widget Update Enroller',
      key: 'ORG_WIDGET_UPDATE_ENROLLER',
      description:
        'Allows Admin and ClientAdmin users to update a ClientUser’s enroller from the Organization widget on the Admin Summary page. Allows "1" or "0"',
    },
    {
      name: 'Product Subscription Autoship Creation',
      key: 'PRODUCT_SUBSCRIPTION_AUTOSHIP_CREATE',
      description:
        'Allows admins to optionally enable autoship creation from the product Subscription tab.',
    },
    {
      name: 'Client 2 Team Page Shared Reports',
      key: 'CLIENT2_TEAM_PAGE_SHARED_REPORTS',
      description:
        'Links Client 2 Team Page widgets to Shared Reports and uses report result counts as widget values.',
    },
  ],
};

function TabPanel(props) {
  const { children, value, index, paddingX, ...other } = props;
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3, px: paddingX ?? 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  paddingX: PropTypes.number,
};

export default function FeatureFlagsSetting() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState();
  const [pairs, setPairs] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const { isTestingDomain, isFlagEnabled } = useFlagsHook();
  const [search, setSearch] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [definitionModal, setDefinitionModal] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchAuthorize = async () => await obelisk().get('Authorize/GetDetails');
  const getFlagsList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFeatureFlag();
      setPairs(data || []);
    } catch (error) {
      setError('Error fetching feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddPair = async () => {
    try {
      setLoading(true);
      await createFeatureFlag({
        key:
          isTestingDomain && !metaData?.key.startsWith('TEST_')
            ? `TEST_${metaData?.key}`
            : metaData?.key || '',
        value: metaData?.value || '0',
        metaType: 'flag',
        isActive: true,
      });
      setMetaData(undefined);
      await getFlagsList();
    } catch (error) {
      setError('Error creating feature flag');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleValue = async (id) => {
    const pair = pairs.find((p) => p.id === id);
    if (!pair) return;

    // Determine new value before API call
    const newValue =
      pair.value === '1' || pair.value === 1 || pair.value === true ? '0' : '1';

    // Optimistic update
    setPairs(pairs.map((p) => (p.id === id ? { ...p, value: newValue } : p)));

    try {
      setLoading(true);
      await createFeatureFlag({
        settingUiId: pair.id,
        key: pair.key,
        value: newValue,
        metaType: 'flag',
      });
      await getFlagsList();
    } catch (error) {
      setError('Error toggling feature flag');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePair = async (id) => {
    try {
      setLoading(true);
      await deleteFeatureFlag(id);
      setPairs(pairs.filter((pair) => pair.id !== id));
    } catch (error) {
      setError('Error deleting feature flag');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRowUpdate = async (newRow) => {
    try {
      setLoading(true);
      await createFeatureFlag({
        settingUiId: newRow.id,
        key: newRow.key,
        value: newRow.value,
        metaType: 'flag',
      });
      await getFlagsList();
      return newRow;
    } catch (error) {
      setError('Error updating feature flag');
      return newRow;
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddPair();
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getDefinitionByKey = (key) => {
    const match =
      featureList.features.find((f) => f.key === key) ||
      featureList.deprecate.find((f) => f.key === key);

    return match?.description || null;
  };

  useEffect(() => {
    const fetchFeatureFlags = async () => await getFlagsList();
    fetchFeatureFlags();
  }, [getFlagsList]);

  const definitionRows = featureList.features
    .filter((flag) => flag.key.toUpperCase().includes(search.toUpperCase()))
    .map((flag, index) => ({
      id: index + 1,
      key: flag.key,
      name: flag.name,
      description: flag.description,
    }));

  const definitionColumns = [
    {
      field: 'key',
      headerName: t('name'),
      flex: 1,
      sortable: true,
    },
    {
      field: 'description',
      headerName: t('definition'),
      flex: 2,
      sortable: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            cursor: 'pointer',
          }}
          onClick={() =>
            setDefinitionModal({
              key: params.row.key,
              description: params.row.description,
            })
          }
        >
          <Typography
            noWrap
            sx={{
              width: '100%',
              fontSize: 14,
              '&:hover': { opacity: 0.8 },
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton onClick={() => setEditItem(params.row)}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-edit'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
            <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
            <path d='M16 5l3 3' />
          </svg>
        </IconButton>
      ),
    },
  ];

  const deprecatedRows = featureList.deprecate
    .filter((d) => d.key.toLowerCase().includes(search.toLowerCase()))
    .map((d) => ({
      id: d.key,
      key: d.key,
      description: d.description,
      name: d.name,
    }));

  const deprecatedColumns = [
    {
      field: 'key',
      headerName: t('name'),
      flex: 1,
    },
    {
      field: 'description',
      headerName: t('definition'),
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            cursor: 'pointer',
          }}
          onClick={() =>
            setDefinitionModal({
              key: params.row.key,
              description: params.row.description,
            })
          }
        >
          <Typography
            noWrap
            sx={{
              width: '100%',
              fontSize: 14,
              '&:hover': { opacity: 0.8 },
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    // {
    //   field: 'status',
    //   headerName: 'Status',
    //   width: 150,
    // },
    // {
    //   field: 'assignee',
    //   headerName: 'Assignee',
    //   width: 150,
    //   valueGetter: (params) => params.row.assignee || '-',
    // },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 100,
      renderCell: (params) => (
        <IconButton onClick={() => setEditItem(params.row)}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className='icon icon-tabler icons-tabler-outline icon-tabler-edit'
          >
            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
            <path d='M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1' />
            <path d='M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z' />
            <path d='M16 5l3 3' />
          </svg>
        </IconButton>
      ),
    },
  ];

  return (
    <PageHeader title={t('featureFlags')} preTitle='Settings'>
      <SettingsNav
        title={t('featureFlags')}
        loading={loading}
        error={error}
        pageId='feature-flags'
      >
        <Box className='card-header'>
          <Box component='span' className='card-title'>
            {t('featureFlags')}
          </Box>
        </Box>
        <Box className='card-body'>
          <Paper sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label='feature flags tabs'
            >
              <Tab label={t('flags')} id='tab-0' aria-controls='tabpanel-0' />
              <Tab label={t('definitions')} id='tab-1' aria-controls='tabpanel-1' />
              {isFlagEnabled('DEPRECATED_FLAGS_MANAGEMENT') && (
                <Tab label={t('deprecated')} id='tab-2' aria-controls='tabpanel-2' />
              )}
            </Tabs>
          </Paper>

          <TabPanel value={tabValue} paddingX={0} index={0}>
            <Typography
              variant='body1'
              color='textSecondary'
              sx={{ margin: '0 0 2em 0' }}
            >
              {t('manageYourApplicationsFeatureFlags')}
            </Typography>
            {isTestingDomain && (
              <Alert severity='error' icon={<RiAlertLine size={18} />} sx={{ mb: 3 }}>
                {t('youAreInATestingEnvironment')}
              </Alert>
            )}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <TextField
                label={t('featureFlagKey')}
                variant='outlined'
                size='small'
                value={metaData?.key || ''}
                onChange={(e) =>
                  setMetaData((arr) => ({
                    ...arr,
                    key: e.target.value
                      .replace(/[\s]/gi, '_')
                      .replace(/[^\w_]/gi, '')
                      .toLocaleUpperCase(),
                  }))
                }
                onKeyPress={handleKeyPress}
                placeholder='Enter key name'
                sx={{ flex: 1, maxWidth: 400 }}
              />
              <TextField
                label={t('featureFlagValue')}
                variant='outlined'
                size='small'
                value={metaData?.value || ''}
                onChange={(e) =>
                  setMetaData((arr) => ({ ...arr, value: e.target.value }))
                }
                onKeyUp={handleKeyPress}
                placeholder='Enter value'
                sx={{ flex: 1, maxWidth: 400 }}
              />
              <Button
                variant='contained'
                color='primary'
                onClick={handleAddPair}
                disabled={!metaData?.key?.trim() || !metaData?.value?.trim()}
              >
                {t('addFlag')}
              </Button>
            </Box>

            {pairs.length > 0 ? (
              <Box sx={{ minHeight: 400, width: '100%' }}>
                <DataGrid
                  rows={pairs.filter((pair) => {
                    if (isTestingDomain) {
                      if (pair.key !== 'TESTING_ENV') {
                        if (!pair.key.startsWith('TEST_')) {
                          return null;
                        }
                      }
                    } else {
                      if (pair.key.startsWith('TEST_')) {
                        return null;
                      }
                    }
                    return pair;
                  })}
                  columns={[
                    {
                      field: 'key',
                      headerName: 'Key',
                      flex: 1,
                      minWidth: 200,
                    },
                    {
                      field: 'value',
                      headerName: 'Value',
                      width: 120,
                      align: 'center',
                      editable: true,
                      renderCell: (params) => {
                        if (
                          params.row.value === '1' ||
                          params.row.value === 1 ||
                          params.row.value === true ||
                          params.row.value === '0' ||
                          params.row.value === 0 ||
                          params.row.value === false
                        ) {
                          return (
                            <Switch
                              checked={
                                params.row.value === '1' ||
                                params.row.value === 1 ||
                                params.row.value === true
                              }
                              onChange={() => handleToggleValue(params.row.id)}
                              color='primary'
                            />
                          );
                        } else {
                          return params.row.value;
                        }
                      },
                    },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      width: 100,
                      sortable: false,
                      align: 'center',
                      renderCell: (params) => (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            width: '100%',
                            gap: 1,
                          }}
                        >
                          {/* ✅ INFO ICON */}
                          {isFlagEnabled('FEATURE_FLAG_DEFINITION_POPUP') && (
                            <Tooltip title={t('definition')}>
                              <IconButton
                                size='small'
                                onClick={() => {
                                  setDefinitionModal({
                                    key: params.row.key,
                                    description: getDefinitionByKey(params.row.key),
                                  });
                                }}
                              >
                                {/* Tabler Info Icon */}
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='20'
                                  height='20'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='currentColor'
                                  strokeWidth='2'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                >
                                  <path d='M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0' />
                                  <path d='M12 9h.01' />
                                  <path d='M11 12h1v4h1' />
                                </svg>
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* DELETE ICON */}
                          <IconButton
                            size='small'
                            color='error'
                            onClick={() => setDeleteItem(params.row)}
                            title='Delete this flag'
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      ),
                    },
                  ]}
                  localeText={getDataGridLocaleText(t)}
                  sx={{
                    '& .MuiTablePagination-toolbar': {
                      justifyContent: 'end',
                      gap: '0',
                    },
                    '& .MuiTablePagination-spacer': {
                      display: 'none',
                    },
                    '& .MuiTablePagination-actions': {
                      marginLeft: 0,
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                      {
                        margin: 0,
                      },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  showToolbar
                  processRowUpdate={handleProcessRowUpdate}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25, page: 0 },
                    },
                  }}
                />
              </Box>
            ) : (
              <Alert sx={{ color: '#999', fontStyle: 'italic', py: 2 }}>
                {t('noFeatureFlagsAddedYet')}
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={tabValue} paddingX={0} index={1}>
            <Box sx={{ px: 0, mx: 0 }}>
              {!isFlagEnabled('FEATURE_FLAG_DEFINITIONS_MANAGEMENT') ? (
                <>
                  {featureList.features.length > 0 && (
                    <Box>
                      <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                        Feature Flags
                      </Typography>
                      <Typography variant='body1' color='textSecondary' sx={{ mb: 3 }}>
                        These are permanent features that can be enabled in the site.
                      </Typography>
                      <Box
                        sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}
                      >
                        {featureList.features.map((flag) => (
                          <React.Fragment key={flag.key}>
                            <Box
                              sx={{
                                p: 2,
                                borderLeft: '3px solid #4caf50',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Typography variant='h6' color='textSecondary'>
                                <code>{`${isTestingDomain && flag.key !== 'TESTING_ENV' ? 'TEST_' : ''}${flag.key}`}</code>
                              </Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                              <Typography
                                variant='subtitle1'
                                sx={{ m: 0, fontWeight: 600 }}
                              >
                                {flag.name}
                              </Typography>
                              <Typography variant='body2' sx={{ m: 0 }}>
                                {flag.description}
                              </Typography>
                            </Box>
                          </React.Fragment>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <AllowByUserRole allowedRoles={['ClientAdmin', 'Admin']}>
                    <Box sx={{ my: 4, borderTop: '1px solid #ccc' }} />
                    {featureList.deprecate.length > 0 && (
                      <Box sx={{ mb: 4 }}>
                        <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                          Deprecated Flags
                        </Typography>
                        <Typography variant='body1' color='textSecondary' sx={{ mb: 3 }}>
                          These are temporary flags that are used to transition features
                          in the site. They will be removed in the future.
                        </Typography>
                        <Box
                          sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}
                        >
                          {featureList.deprecate.map((flag) => (
                            <React.Fragment key={flag.key}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderLeft: '3px solid #ff9800',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant='h6' color='textSecondary'>
                                  <code>{`${isTestingDomain ? 'TEST_' : ''}${flag.key}`}</code>
                                </Typography>
                              </Box>
                              <Box sx={{ p: 2 }}>
                                <Typography
                                  variant='subtitle1'
                                  sx={{ m: 0, fontWeight: 600 }}
                                >
                                  {flag.name}
                                </Typography>
                                <Typography variant='body2' sx={{ m: 0 }}>
                                  {flag.description}
                                </Typography>
                              </Box>
                            </React.Fragment>
                          ))}
                        </Box>
                      </Box>
                    )}
                    <Button variant='contained' color='primary' onClick={fetchAuthorize}>
                      Test
                    </Button>
                  </AllowByUserRole>
                </>
              ) : (
                <DataGrid
                  rows={definitionRows}
                  columns={definitionColumns}
                  localeText={getDataGridLocaleText(t)}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    // sorting: {
                    //   sortModel: [{ field: 'key', sort: 'asc' }],
                    // },
                  }}
                  sx={{
                    '& .MuiDataGrid-toolbar button[aria-label="Columns"], & .MuiDataGrid-toolbar button[aria-label="Export"]':
                      {
                        display: 'none',
                      },
                    '& .MuiDataGrid-toolbar .MuiDivider-root': {
                      display: 'none',
                    },
                    '& .MuiTablePagination-toolbar': {
                      justifyContent: 'end',
                      gap: '0',
                    },
                    '& .MuiTablePagination-spacer': {
                      display: 'none',
                    },
                    '& .MuiTablePagination-actions': {
                      marginLeft: 0,
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                      {
                        margin: 0,
                      },
                  }}
                  showToolbar
                  slots={{
                    toolbar: () => (
                      <CustomToolbar placeholder={t('searchFeatureFlags')} />
                    ),
                  }}
                  // localeText={{
                  //   MuiTablePagination: {
                  //     labelRowsPerPage: t('rowsPerPage'),
                  //     labelDisplayedRows: ({ from, to, count }) =>
                  //       `${from}-${to} ${t('of')} ${
                  //         count !== -1 ? count : `${t('moreThan')} ${to}`
                  //       }`,
                  //   },
                  // }}
                  disableColumnMenu
                  autoHeight
                />
              )}
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} paddingX={0} index={2}>
            <Box>
              <AllowByUserRole allowedRoles={['ClientAdmin', 'Admin']}>
                <DataGrid
                  rows={deprecatedRows}
                  columns={deprecatedColumns}
                  localeText={getDataGridLocaleText(t)}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    // sorting: {
                    //   sortModel: [{ field: 'key', sort: 'asc' }],
                    // },
                  }}
                  sx={{
                    '& .MuiDataGrid-toolbar button[aria-label="Columns"], & .MuiDataGrid-toolbar button[aria-label="Export"]':
                      {
                        display: 'none',
                      },
                    '& .MuiDataGrid-toolbar .MuiDivider-root': {
                      display: 'none',
                    },
                    '& .MuiTablePagination-toolbar': {
                      justifyContent: 'end',
                      gap: '0px',
                    },
                    '& .MuiTablePagination-spacer': {
                      display: 'none',
                    },
                    '& .MuiTablePagination-actions': {
                      marginLeft: 0,
                    },
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                      {
                        margin: 0,
                      },
                  }}
                  showToolbar
                  slots={{
                    toolbar: () => (
                      <CustomToolbar placeholder={t('searchDeprecatedFlags')} />
                    ),
                  }}
                  disableColumnMenu
                  autoHeight
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end', // ✅ pushes button to right
                    mt: 2,
                  }}
                >
                  <Button variant='contained' color='primary' onClick={fetchAuthorize}>
                    {t('test')}
                  </Button>
                </Box>
              </AllowByUserRole>
            </Box>
          </TabPanel>
        </Box>
      </SettingsNav>
      {definitionModal && (
        <ModalView
          fader={'show'}
          onCloseEvent={() => setDefinitionModal(null)}
          centered
          size='md'
          disableClose={true}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 2,
            }}
          >
            <Typography variant='h6' sx={{ fontWeight: 600 }}>
              {t('featureFlagDefinition')}
            </Typography>

            <IconButton
              onClick={() => setDefinitionModal(null)}
              size='small'
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              px: 1,
              pb: 1,

              '&.MuiDialogContent-root': {
                paddingTop: 1,
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: 'background.default',

                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant='caption'
                  sx={{
                    color: 'text.secondary',

                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                >
                  {t('key').toUpperCase()}
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  {definitionModal?.key}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant='caption'
                  sx={{
                    color: 'text.secondary',

                    fontWeight: 600,
                    fontSize: '16px',
                  }}
                >
                  {t('definition').toUpperCase()}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '14px',
                    color: definitionModal?.description
                      ? 'text.primary'
                      : 'text.secondary',
                  }}
                >
                  {definitionModal?.description || t('noDefinitionsFound')}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
        </ModalView>
      )}
      {editItem && (
        <FeatureFlagEditModal
          open={editItem ? true : false}
          onClose={() => setEditItem(null)}
          data={editItem}
          setData={setEditItem}
          type={tabValue === 1 ? 'definition' : tabValue === 2 ? 'deprecated' : undefined}
          onSave={(payload) => {
            console.log(payload);
            setEditItem(null);
            getFlagsList();
          }}
        />
      )}
      {deleteItem && (
        <FeatureFlagDeleteModal
          open={Boolean(deleteItem)}
          flagKey={deleteItem.key}
          onClose={() => setDeleteItem(null)}
          onConfirm={async () => {
            const id = deleteItem.id;

            setDeleteItem(null);

            await handleDeletePair(id);
          }}
        />
      )}
    </PageHeader>
  );
}
